import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trophy, TrendingUp, TrendingDown, Download, SlidersHorizontal } from "lucide-react";

/** Phase D · Performance dashboard — zones, top / moving up / close to green / needs attention, thresholds editor, Excel export. */
export default function PerformanceDashboard({ open, onOpenChange, outlets }) {
  const [period, setPeriod] = useState("month");
  const [outletId, setOutletId] = useState("");
  const [d, setD] = useState(null);
  const [thresholds, setThresholds] = useState({ green: 80, yellow: 60 });
  const [params, setParams] = useState([]);
  const [showConfig, setShowConfig] = useState(false);
  const reload = async () => {
    if (!open) return;
    try {
      const q = new URLSearchParams({ period }); if (outletId) q.set("outlet_id", outletId);
      const [r, t, p] = await Promise.all([
        api.get(`/perf/dashboard?${q}`).then(x => x.data),
        api.get("/perf-thresholds").then(x => x.data),
        api.get("/perf-parameters").then(x => x.data),
      ]);
      setD(r); setThresholds(t); setParams(p);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => { reload(); }, [open, period, outletId]);

  const downloadExcel = async () => {
    try {
      const t = localStorage.getItem("cc_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/perf/excel?period=${period}`,
        { headers: { Authorization: `Bearer ${t}` }});
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `chaichaska_perf_${period}.xlsx`; a.click();
      toast.success("Excel downloaded");
    } catch (e) { toast.error(e.message); }
  };

  const saveThresholds = async () => {
    try { await api.post("/perf-thresholds", thresholds); toast.success("Thresholds saved"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const saveParam = async (p) => {
    try { await api.patch(`/perf-parameters/${p.id}`, { weightage: Number(p.weightage) }); toast.success("Weightage saved"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const ZoneBadge = ({ z, pct }) => (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono2 ${z === "GREEN" ? "bg-emerald-100 text-emerald-700" : z === "YELLOW" ? "bg-amber-100 text-amber-800" : z === "RED" ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600"}`}>
      {z === "NO_DATA" ? "NO DATA" : `${z} · ${pct}%`}
    </span>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-[#E4D6A8] max-w-6xl max-h-[92vh] overflow-y-auto" style={{ color: "#2b1a0d" }} data-testid="perf-root">
        <DialogHeader><DialogTitle className="font-display text-2xl">Performance Dashboard</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-3 items-end mb-3 pb-3 border-b" style={{borderColor:"#F3E7C4"}}>
          <div>
            <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>Period</label>
            <select data-testid="perf-period" value={period} onChange={e => setPeriod(e.target.value)} className="h-9 px-2 rounded border bg-white" style={{borderColor:"#E4D6A8"}}>
              <option value="week">Week</option><option value="month">Month</option>
              <option value="quarter">Quarter</option><option value="year">Year</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>Outlet</label>
            <select data-testid="perf-outlet" value={outletId} onChange={e => setOutletId(e.target.value)} className="h-9 px-2 rounded border bg-white min-w-[160px]" style={{borderColor:"#E4D6A8"}}>
              <option value="">All outlets</option>
              {outlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <Button data-testid="perf-config" size="sm" variant="outline" onClick={() => setShowConfig(v => !v)}><SlidersHorizontal size={13} className="mr-1" /> Configure</Button>
          <Button data-testid="perf-excel" onClick={downloadExcel} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
            <Download size={13} className="mr-1" /> Excel
          </Button>
        </div>

        {showConfig && (
          <div className="rounded-xl border p-3 mb-3 bg-[#FFF8E7]" style={{borderColor:"#E4D6A8"}} data-testid="perf-config-panel">
            <div className="text-[10px] font-mono2 uppercase tracking-widest mb-2" style={{color:"#B4841F"}}>Zone thresholds (%)</div>
            <div className="flex items-end gap-2 mb-3">
              <div><label className="text-xs">Green ≥</label><Input data-testid="thr-green" type="number" value={thresholds.green} onChange={e => setThresholds(t => ({...t, green: e.target.value}))} className="w-24 h-9" /></div>
              <div><label className="text-xs">Yellow ≥</label><Input data-testid="thr-yellow" type="number" value={thresholds.yellow} onChange={e => setThresholds(t => ({...t, yellow: e.target.value}))} className="w-24 h-9" /></div>
              <Button size="sm" data-testid="thr-save" onClick={saveThresholds} className="bg-[#F5C242] text-black hover:bg-[#e9a722]">Save</Button>
            </div>
            <div className="text-[10px] font-mono2 uppercase tracking-widest mb-2" style={{color:"#B4841F"}}>Parameter weightages</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {params.map(p => (
                <div key={p.id} className="flex items-center gap-1 text-xs">
                  <div className="flex-1 truncate">{p.name}</div>
                  <Input data-testid={`param-w-${p.id}`} type="number" value={p.weightage} onChange={e => setParams(ps => ps.map(x => x.id === p.id ? {...x, weightage: e.target.value} : x))} className="w-16 h-8" />
                  <Button size="sm" variant="ghost" onClick={() => saveParam(p)}>✓</Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {!d ? <div className="py-10 text-center text-xs" style={{color:"#7a6349"}}>Loading…</div> : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <Kpi label="Active staff" value={d.totals.staff} />
              <Kpi label="🟢 Green" value={d.totals.green} tone="pos" />
              <Kpi label="🟡 Yellow" value={d.totals.yellow} tone="mid" />
              <Kpi label="🔴 Red" value={d.totals.red} tone="neg" />
              <Kpi label="Avg %" value={d.totals.avg} big />
            </div>
            <div className="grid md:grid-cols-2 gap-3 mb-4">
              <RankList title="Top performers" icon={Trophy} rows={d.top.slice(0,5)} field="overall" suffix="%" ZoneBadge={ZoneBadge} testid="perf-top" />
              <RankList title="Moving up" icon={TrendingUp} rows={d.moving_up.slice(0,5)} field="delta" prefix="+" suffix="%" ZoneBadge={ZoneBadge} testid="perf-moving" />
              <RankList title="Close to green" rows={d.close_to_green} field="overall" suffix="%" ZoneBadge={ZoneBadge} testid="perf-close" hint={`Green ≥ ${d.thresholds.green}%`} />
              <RankList title="Needs attention" icon={TrendingDown} rows={d.needs_attention} field="delta" suffix="%" ZoneBadge={ZoneBadge} testid="perf-attention" />
            </div>

            <div className="rounded-xl border overflow-x-auto" style={{borderColor:"#E4D6A8"}}>
              <table className="w-full text-sm min-w-[720px]">
                <thead className="bg-[#FFF8E7]"><tr className="text-[10px] font-mono2 uppercase tracking-widest" style={{color:"#7a6349"}}>
                  <th className="text-left py-2 px-2">Outlet</th><th className="text-right py-2 px-2">Staff</th>
                  <th className="text-right py-2 px-2">🟢</th><th className="text-right py-2 px-2">🟡</th>
                  <th className="text-right py-2 px-2">🔴</th><th className="text-right py-2 px-2">Avg %</th>
                  <th className="text-left py-2 px-2">Top</th><th className="text-left py-2 px-2">Lowest</th>
                </tr></thead>
                <tbody>
                  {d.outlet_breakdown.map(b => (
                    <tr key={b.outlet_id} className="border-t" style={{borderColor:"#F3E7C4"}} data-testid={`perf-outlet-${b.outlet_id}`}>
                      <td className="py-1.5 px-2 font-medium">{b.outlet_name}</td>
                      <td className="py-1.5 px-2 text-right">{b.staff}</td>
                      <td className="py-1.5 px-2 text-right text-emerald-700">{b.green}</td>
                      <td className="py-1.5 px-2 text-right text-amber-700">{b.yellow}</td>
                      <td className="py-1.5 px-2 text-right text-red-700">{b.red}</td>
                      <td className="py-1.5 px-2 text-right font-bold">{b.avg}%</td>
                      <td className="py-1.5 px-2">{b.top ? `${b.top.name} · ${b.top.overall}%` : "—"}</td>
                      <td className="py-1.5 px-2">{b.low ? `${b.low.name} · ${b.low.overall}%` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Kpi({ label, value, tone, big }) {
  const c = tone === "pos" ? "#0a7a3b" : tone === "neg" ? "#c0392b" : tone === "mid" ? "#c78200" : "#2b1a0d";
  return (
    <div className="rounded-xl p-2 border" style={{borderColor:"#E4D6A8", background:"#FFFDF6"}}>
      <div className="text-[10px] font-mono2 uppercase tracking-widest" style={{color:"#7a6349"}}>{label}</div>
      <div className={big ? "font-display text-2xl" : "font-bold text-lg"} style={{color:c}}>{value}</div>
    </div>
  );
}
function RankList({ title, icon: Ic, rows, field, prefix = "", suffix = "", ZoneBadge, testid, hint }) {
  return (
    <div className="rounded-xl border bg-white p-3" style={{borderColor:"#E4D6A8"}} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-display text-sm flex items-center gap-1" style={{color:"#2b1a0d"}}>{Ic && <Ic size={13} style={{color:"#B4841F"}} />}{title}</div>
        {hint && <span className="text-[10px] opacity-60">{hint}</span>}
      </div>
      {rows.length === 0 ? <div className="text-xs opacity-60 py-2">— no data —</div> : rows.map(r => (
        <div key={r.staff_id} className="flex items-center gap-2 py-1 text-sm border-t first:border-t-0" style={{borderColor:"#F3E7C4"}}>
          <div className="flex-1 truncate"><b>{r.staff_name}</b> <span className="opacity-50 text-xs">{r.outlet_name}</span></div>
          <b>{prefix}{r[field]}{suffix}</b>
          <ZoneBadge z={r.zone} pct={r.overall} />
        </div>
      ))}
    </div>
  );
}
